import { cache } from 'react'
import { gql } from '@apollo/client'
import { getClient } from '@/lib/apollo-client'
import { IngredientDetailDocument } from 'graphql/generated/graphql'

export const RECIPE_PREVIEW_LIMIT = 4

// Not codegen'd — this query is only ever called directly against
// getClient()/useQuery, never through a generated hook.
export const RECIPES_FOR_DIRECTORY_INGREDIENT = gql`
  query RecipesForDirectoryIngredient($slug: String!, $limit: Int) {
    recipesForDirectoryIngredient(slug: $slug, limit: $limit) {
      total
      recipes {
        recipe_id
        recipe_name
        recipe_img_url
        resourcePath
        thumbnailPath
        author_name
        total_time
        average_recipe_rating
        slug
        user {
          user_id
          first_name
          last_name
          full_name
          profile_photo_resource
          default_listing {
            photo
          }
        }
        recipe_image {
          thumbnail_path
          resource_path
        }
        recipe_comments {
          id
        }
        recipe_ingredients {
          record_id
        }
      }
    }
  }
`

// cache()-wrapped so page.tsx and layout.tsx dedupe to one actual network
// call per request instead of fetching the same ingredient twice.
//
// errorPolicy 'all' + catch: producer product cards link to /ingredients/{product.slug}.
// Many catalog products (e.g. 372-shishito) are not directory ingredients yet, so the
// API returns "record not found". Default Apollo policy throws on that GraphQL error,
// which turned the SSR page into a 500 / hydration crash instead of the empty state.
export const fetchIngredientBySlug = cache(async (slug: string) => {
  try {
    const client = getClient()
    const { data } = await client.query({
      query: IngredientDetailDocument,
      variables: { slug },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    })
    return data?.ingredientDetail ?? null
  } catch {
    return null
  }
})

// Parent/child roll-up: on a variety page (Jonathan Apples) growers of the parent (Apples);
// on a parent page (Apples) growers of its varieties. Neither set overlaps the exact growers in
// `ingredient.listings`. Kept as a SEPARATE query (not folded into IngredientDetailDocument) so a
// failure -- including the API not having these fields yet -- can never take down the ingredient
// page; it just yields no extra growers.
const RELATED_GROWERS_QUERY = gql`
  query IngredientRelatedGrowers($slug: String) {
    ingredientDetail(slug: $slug) {
      parent_name
      parent_listings {
        listing_id
        name
        slug
        photo
        user_type
        city
        state
        enhanced
        verified
        membership_type
        business_description_completed
        products_completed
        vendors_completed
        selling_locations_completed
        sales_channels_completed
      }
      child_listings {
        listing_id
        name
        slug
        photo
        user_type
        city
        state
        enhanced
        verified
        membership_type
        business_description_completed
        products_completed
        vendors_completed
        selling_locations_completed
        sales_channels_completed
      }
    }
  }
`

export type RelatedGrowers = {
  parentName: string | null
  parentListings: any[]
  childListings: any[]
}

export const fetchRelatedGrowers = cache(async (slug: string): Promise<RelatedGrowers> => {
  const empty = { parentName: null, parentListings: [], childListings: [] }
  try {
    const client = getClient()
    const { data } = await client.query({
      query: RELATED_GROWERS_QUERY,
      variables: { slug },
      fetchPolicy: 'no-cache',
    })
    const d = data?.ingredientDetail
    const parentName = d?.parent_name ?? null
    return {
      // Without the parent's name we can't label parent-level growers honestly ("Grows ___").
      parentName,
      parentListings: parentName ? d?.parent_listings ?? [] : [],
      childListings: d?.child_listings ?? [],
    }
  } catch {
    return empty
  }
})

export const fetchIngredientRecipes = cache(async (slug: string, limit: number) => {
  const client = getClient()
  const { data } = await client
    .query({
      query: RECIPES_FOR_DIRECTORY_INGREDIENT,
      variables: { slug, limit },
      fetchPolicy: 'no-cache',
    })
    .catch(() => ({ data: null }))
  return data?.recipesForDirectoryIngredient ?? { total: 0, recipes: [] }
})
